import { Dispatch, SetStateAction, useCallback } from 'react';
import { IconEuro } from '@iwoca/icons';
import { Card, Select, Input, Tooltip } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useLocale } from 'hooks/useLocale';
import { useFormContext } from 'react-hook-form';
import { MONTHS } from 'utils/applications/signup.config';
import InputFormatter from 'utils/inputFormatters/inputFormatters';
import signupFormStyles from '../../SignupForm/SignupForm.module.css';
import deFormStyles from '../NewSignupFormDE.module.css';
import { TSignupFormDataDE } from '../NewSignupFormDE.types';
import { CompanyInputDE } from './CompanyInputDE/CompanyInputDE';

type TProps = {
  searchedCompaniesCount: number;
  setSearchedCompaniesCount: Dispatch<SetStateAction<number>>;
};

export const CompanyDetailsDE = ({
  searchedCompaniesCount,
  setSearchedCompaniesCount,
}: TProps) => {
  const { isDE } = useLocale();

  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<TSignupFormDataDE>();

  const formatCurrency = useCallback(() => {
    return InputFormatter.Currency(
      watch('company.last12MonthsTurnover') || '',
      isDE,
    );
  }, [watch, isDE]);

  const { checkIntroducerPermission } = useIntroducerContext();
  const isDisabled = !checkIntroducerPermission('can_create_applications');

  return (
    <Card className={signupFormStyles.formSection}>
      <h2>
        <Trans>Company details</Trans>
      </h2>
      <div className={signupFormStyles.cardContents}>
        <CompanyInputDE
          searchedCompaniesCount={searchedCompaniesCount}
          setSearchedCompaniesCount={setSearchedCompaniesCount}
        />

        <div className={deFormStyles.inlineInputFields}>
          <div className={deFormStyles.inlineInputSpan}>
            <Select
              {...register('company.tradingFromMonth')}
              id="tradingFromMonth"
              label={t`Trading date`}
              placeholder={t`Month`}
              error={errors?.company?.tradingFromMonth?.message}
              disabled={isDisabled}
              aria-disabled={isDisabled}
            >
              {MONTHS().map(({ label, value }) => (
                <option key={label} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <Input
            {...register('company.tradingFromYear')}
            id="tradingFromYear"
            placeholder="JJJJ"
            data-testid="trading-from-year"
            error={errors?.company?.tradingFromYear?.message}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          />
        </div>

        <div className={deFormStyles.inputField}>
          <Input
            {...register('company.last12MonthsTurnover')}
            id="last12MonthsTurnover"
            iconRight={<IconEuro />}
            value={formatCurrency()}
            data-testid="last-12-months-turnover"
            label={
              <span className={deFormStyles.tooltipLabel}>
                <Trans>Last 12 months turnover</Trans>
                <Tooltip
                  content={t`A rough estimate is enough for us. If your company has been operating for less than a year, you can simply state the previous sales volume.`}
                />
              </span>
            }
            error={errors?.company?.last12MonthsTurnover?.message}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          />
        </div>

        <div className={deFormStyles.inputField}>
          <Input
            {...register('company.website')}
            id="website"
            placeholder="z.B. www.mustermann.de"
            data-testid="website"
            label={
              <>
                <Trans>Website</Trans>{' '}
                <span className={deFormStyles.optionalText}>
                  (<Trans>optional</Trans>)
                </span>
              </>
            }
            error={errors?.company?.website?.message}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          />
        </div>
      </div>
    </Card>
  );
};
